/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import {
    TextField,
    TextFieldStateless,
    FlexRow,
    FlexCol,
    DatePicker,
    localToday,
    Alert,
    withLDConsumer,
} from 'sarsaparilla';
import { today } from '@internationalized/date';
import { formatDate } from 'ui-activitypass/src/utils/upsell_utils';
import { FacilityDefaults } from '../props';
import { getSeasonDates } from '../utils';
import { validToDate } from '../utils/duration_utils';
import {
    isDailyPassType,
    isFreeDay,
    datesBlockedV2,
    getBlackoutPeriodForDate,
    getBookingWindowMonths,
    parseDate,
    isValidDateFormat,
} from '../utils/date_time_utils';

export const getEndDates = (passType, startCalendarDate, endDate, seasonEnd) => {
    if (!startCalendarDate) {
        return {
            validEndDate: '',
            isEmptyStartDate: true,
        };
    }
    let validEndDate = today();
    const typeSegments = passType.features['day-segment'];
    if (typeSegments) {
        validEndDate = isValidDateFormat(endDate) ? parseDate(endDate) : validEndDate;
    } else {
        validEndDate = validToDate(startCalendarDate, passType);
    }

    if (passType.features['start-date']) {
        if (startCalendarDate.compare(validEndDate) > 0) {
            validEndDate = startCalendarDate.copy();
        }
    }

    if (['season', 'seasonal'].includes(passType.duration)) {
        validEndDate = isValidDateFormat(seasonEnd) ? parseDate(seasonEnd) : validEndDate;
        if (!isValidDateFormat(seasonEnd) && seasonEnd?.split) {
            const [year, month, day] = seasonEnd.split('-');
            const adjustedYear = parseInt(year, 10) + 1;
            const validEndDateStr = `${adjustedYear}-${month}-${day}`;
            validEndDate = isValidDateFormat(validEndDateStr)
                ? parseDate(validEndDateStr)
                : validEndDate;
        }
    }

    if (isDailyPassType(passType?.duration) && !startCalendarDate) {
        return {
            validEndDate: '',
            isEmptyStartDate: true,
        };
    }

    return { validEndDate, isEmptyStartDate: false };
};

export class StartDateForm extends PureComponent {
    static propTypes = {
        onChangeStart: PropTypes.func,
        onChangeEnd: PropTypes.func,
        passType: PropTypes.object,
        startDate: PropTypes.string,
        endDate: PropTypes.string,
        flags: PropTypes.object,
    };
    static defaultProps = {
        number: 1,
        startDate: today().toString(),
        endDate: today().toString(),
        facility: FacilityDefaults,
        flags: {},
    };

    daySegmentsTimeout;

    constructor(props) {
        super(props);
        this.state = {
            isInclusionsModified: props.isExistingReservation,
            included: {},
            closureReason: {},
        };
        this.state.included = props.segments || {};
    }

    componentDidMount() {
        this.props.onChangeStart(this.props.startDate, true);
        this.props.onChangeEnd(this.props.endDate, true);
        this.daySegmentsTimeout = setTimeout(this.handleChangeDaySegments, 250);
    }

    componentDidUpdate(prevProps) {
        // If the date range has changed, regenerate dates.
        if (
            prevProps.startDate !== this.props.startDate ||
            prevProps.endDate !== this.props.endDate
        ) {
            const { startDate, onChangeEnd, passType } = this.props;
            const typeSegments = passType.features['day-segment'];
            //If the end date is disabled & is not a datepicker calculate as well as set the end date
            if (!typeSegments && this.props.startDate) {
                const startCalendarDate = isValidDateFormat(startDate)
                    ? parseDate(startDate)
                    : today();

                const endCalendarDate = validToDate(startCalendarDate, passType);

                onChangeEnd(endCalendarDate.toString(), true);
            }
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState(
                {
                    included: {},
                },
                this.handleChangeDaySegments
            );
        }
    }

    componentWillUnmount() {
        clearTimeout(this.daySegmentsTimeout);
    }

    onStartDateChanged = (date) => {
        this.props.onChangeStart(parseDate(date).toString(), true);
    };

    onStartDateChangedNew = (value, validation) => {
        if (!value || validation?.isInvalid) {
            this.props.onChangeStart('');
            return;
        }

        this.props.onChangeStart(value.toString(), true);
    };

    onEndDateChangedNew = (value, validation) => {
        if (!value || validation?.isInvalid) {
            this.props.onChangeEnd('');
            return;
        }
        this.props.onChangeEnd(value.toString(), true);
    };

    getTooltipContent = (day) => {
        const { facility, passType } = this.props;
        const maxDate = localToday.add({
            months: getBookingWindowMonths(facility?.agency),
        });

        if (datesBlockedV2(facility, passType, true)(day) === 'available') {
            return '';
        }

        if (day.compare(maxDate) > 0) {
            return `This ${facility.facility_type} is not yet released (NR)`;
        }

        const closure = getBlackoutPeriodForDate(facility, passType, day);
        if (closure) {
            return `Closure Reason: ${closure?.reason}`;
        }

        if (isFreeDay(facility, day)) {
            return 'Fee-Free Day';
        }

        return 'Unavailable';
    };

    dateValidator = (id, value, label) => {
        const { facility, passType } = this.props;
        this.setState((prevState) => ({
            closureReason: {
                ...prevState.closureReason,
                [id]: null,
            },
        }));

        // updateAddToCartState is specific to AP external UI, won't be provided by widget-ui
        if (this.props.updateAddToCartState) this.props.updateAddToCartState(false);
        if (datesBlockedV2(facility, passType, true)(value) !== 'available') {
            const reason = this.getTooltipContent(value);
            if (reason !== '') {
                this.setState((prevState) => ({
                    closureReason: {
                        ...prevState.closureReason,
                        [id]: reason,
                    },
                }));
            }
            if (this.props.updateAddToCartState) this.props.updateAddToCartState(true);
            return {
                isInvalid: true,
                errorMessage: `${label} on a closure day`,
            };
        }

        return {
            isInvalid: false,
            errorMessage: '',
        };
    };

    handleChangeDaySegments = () => {
        this.props.onChangeDaySegments(this.state.included);
    };

    renderDayNew = (day) => {
        const { facility, passType } = this.props;

        const maxDate = localToday.add({
            months: getBookingWindowMonths(facility?.agency),
        });
        // If in the past, don't apply a styled day
        if (day.compare(localToday) < 0) {
            return null;
        }

        const blockedCheckResult = datesBlockedV2(facility, passType, true)(day);
        if (blockedCheckResult !== 'available') {
            return blockedCheckResult;
        }
        return day.compare(maxDate) > 0 ? 'notYetReleased' : blockedCheckResult;
    };

    renderInitialVisibleMonth = () => {
        const { facility, passType } = this.props;
        let initialCalendarDate = today();
        const isDayBlockedV2 = datesBlockedV2(facility, passType, true);
        while (
            isDayBlockedV2(initialCalendarDate) !== 'available' &&
            initialCalendarDate?.compare(today().add({ years: 1 })) < 0
        ) {
            initialCalendarDate = initialCalendarDate.add({ days: 1 });
        }
        return initialCalendarDate;
    };

    render() {
        const { isInternal, number, passType, startDate, endDate, facility } = this.props;
        const isDayBlockedV2 = datesBlockedV2(facility, passType, false);
        const typeSegments = passType.features['day-segment'];
        const placeholderValue = this.renderInitialVisibleMonth();

        const todayDate = today();
        let startDateCalendar = todayDate;
        let startDateInput;
        const [seasonStart, seasonEnd] = getSeasonDates(
            passType.periods,
            undefined,
            this.props?.flags?.useApDateFix
        );
        if (passType.features['start-date']) {
            startDateCalendar = startDate ? parseDate(startDate) : startDateCalendar;
            // Advance days until we find a non-blocked day.
            if (startDateCalendar && isDayBlockedV2(startDateCalendar) !== 'available') {
                while (
                    isDayBlockedV2(startDateCalendar) !== 'available' &&
                    startDateCalendar.compare(todayDate.add({ years: 1 })) <= 0
                ) {
                    startDateCalendar = startDateCalendar.add({ days: 1 });
                }
                this.onStartDateChanged(startDateCalendar);
            }

            const isAnnualExactDate = passType.duration === '12-month';
            const isSixMonthExactDate = passType.duration === '6-month';
            const startDateId = `start-date-${number}`;

            startDateInput = (
                <>
                    <DatePicker
                        isRequired
                        label={typeSegments ? 'Arrival Date' : 'Start Date'}
                        id={startDateId}
                        isDisabled={
                            (isInternal && !!typeSegments) ||
                            isAnnualExactDate ||
                            isSixMonthExactDate
                        }
                        showClearDates={false}
                        minValue={localToday}
                        maxValue={localToday.add({
                            months: getBookingWindowMonths(facility?.agency),
                        })}
                        defaultValue={startDate ? parseDate(startDate) : undefined}
                        onChange={this.onStartDateChangedNew}
                        isDateUnavailable={(date) => {
                            return isDayBlockedV2(date) !== 'available';
                        }}
                        placeholderValue={placeholderValue}
                        getDayType={this.renderDayNew}
                        getTooltipContent={this.getTooltipContent}
                        validator={(value, label) =>
                            this.dateValidator(startDateId, value, label)
                        }
                    />
                    {this.state.closureReason &&
                        this.state.closureReason[startDateId] && (
                            <Alert type="error" hasAriaLive>
                                {this.state.closureReason[startDateId]}
                            </Alert>
                        )}
                </>
            );
        } else {
            // If seasonal pricing, override completely
            if (['season', 'seasonal'].includes(passType.duration)) {
                startDateCalendar = isValidDateFormat(seasonStart)
                    ? parseDate(seasonStart)
                    : todayDate;
            } else if (isDailyPassType(passType.duration)) {
                startDateCalendar = isValidDateFormat(startDate)
                    ? parseDate(startDate)
                    : todayDate;
            } else {
                startDateCalendar = todayDate;
            }

            startDateInput = (
                <TextField
                    id={`start-date-${number}`}
                    value={formatDate(startDateCalendar)}
                    label={typeSegments ? 'Arrival Date' : 'Start Date'}
                    disabled
                />
            );
        }

        const { validEndDate: endCalendarDate, isEmptyStartDate } = getEndDates(
            passType,
            startDateCalendar,
            endDate,
            seasonEnd
        );
        const endDateId = `end-date-${number}`;

        return (
            <div>
                <FlexRow>
                    <FlexCol xl="auto" className="rec-form-item-wrap">
                        {startDateInput}
                    </FlexCol>
                    <FlexCol xl="auto">
                        {typeSegments ? (
                            <>
                                <DatePicker
                                    isRequired
                                    label={typeSegments ? 'Departure Date' : 'End Date'}
                                    id={endDateId}
                                    minValue={startDateCalendar}
                                    maxValue={todayDate
                                        .add({
                                            months: getBookingWindowMonths(
                                                facility?.agency
                                            ),
                                        })
                                        .subtract({ days: 1 })}
                                    defaultValue={endCalendarDate || undefined}
                                    onChange={this.onEndDateChangedNew}
                                    isDateUnavailable={(date) => {
                                        return (
                                            datesBlockedV2(
                                                facility,
                                                passType,
                                                false
                                            )(date) !== 'available'
                                        );
                                    }}
                                    getDayType={this.renderDayNew}
                                    getTooltipContent={this.getTooltipContent}
                                    validator={(value, label) =>
                                        this.dateValidator(endDateId, value, label)
                                    }
                                />
                                {this.state.closureReason &&
                                    this.state.closureReason[endDateId] && (
                                        <Alert type="error" hasAriaLive>
                                            Closure {this.state.closureReason[endDateId]}
                                        </Alert>
                                    )}
                            </>
                        ) : (
                            <TextFieldStateless
                                id={`through-date-${number}`}
                                className="rec-form-item-wrap"
                                value={
                                    !isEmptyStartDate
                                        ? formatDate(endCalendarDate)
                                        : 'Date'
                                }
                                label="Valid Through"
                                isDisabled
                            />
                        )}
                    </FlexCol>
                </FlexRow>
            </div>
        );
    }
}

// cSpell:ignore activitypass
export default withLDConsumer()(StartDateForm);
